import { createCard } from './card';
import { RANK_ORDER_STANDARD, STANDARD_SUITS } from './constants';
import { Rank, Suit } from './enums';
import { Card } from './types';

export function createStandardDeck(): Card[] {
  return RANK_ORDER_STANDARD.flatMap((rank) => STANDARD_SUITS.map((suit) => createCard(rank, suit)));
}

/**
 * Creates a deck with jokers added
 */
export function createDeckWithJokers(jokerCount: number): Card[] {
  const deck = createStandardDeck();

  for (let i = 0; i < jokerCount; i++) {
    deck.push(createCard(Rank.JOKER, Suit.WILD));
  }

  return deck;
}

/**
 * Result of drawing cards from a deck
 */
export interface DrawResult {
  readonly drawn: Card[];
  readonly remaining: Card[];
}

/**
 * Draws cards from the top of the deck
 * Returns the drawn cards and the remaining deck
 */
export function drawCards(deck: readonly Card[], count: number): DrawResult {
  if (count > deck.length) {
    throw new Error(`Cannot draw ${count} cards from deck with ${deck.length} cards`);
  }

  return {
    drawn: deck.slice(0, count),
    remaining: deck.slice(count),
  };
}

/**
 * Shuffles a deck using Fisher-Yates algorithm
 * Returns a new shuffled array (does not mutate original)
 */
export function shuffleDeck(deck: readonly Card[]): Card[] {
  const shuffled = [...deck];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

/**
 * Shuffles a deck with a seeded random number generator (for reproducibility)
 */
export function shuffleDeckSeeded(deck: readonly Card[], seed: number): Card[] {
  const shuffled = [...deck];

  // Simple seeded PRNG (mulberry32)
  let state = seed;
  const random = (): number => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}
